import { pipe } from '@wiko/functional';
import { RpcSubscriptionsChannelCreator, RpcSubscriptionsTransport } from '@wiko/rpc-subscriptions-spec';
import { ClusterUrl } from '@wiko/rpc-types';

import {
    RpcSubscriptionsChannelCreatorDevnet,
    RpcSubscriptionsChannelCreatorFromClusterUrl,
    RpcSubscriptionsChannelCreatorMainnet,
    RpcSubscriptionsChannelCreatorTestnet,
    RpcSubscriptionsTransportDevnet,
    RpcSubscriptionsTransportFromClusterUrl,
    RpcSubscriptionsTransportMainnet,
    RpcSubscriptionsTransportTestnet,
} from './rpc-subscriptions-clusters';
import { getRpcSubscriptionsTransportWithSubscriptionCoalescing } from './rpc-subscriptions-coalescer';

export type DefaultRpcSubscriptionsTransportConfig<TClusterUrl extends ClusterUrl> = Readonly<{
    createChannel: RpcSubscriptionsChannelCreatorFromClusterUrl<TClusterUrl, unknown, unknown>;
}>;

export function createDefaultRpcSubscriptionsTransport<TClusterUrl extends ClusterUrl>({
    createChannel,
}: DefaultRpcSubscriptionsTransportConfig<TClusterUrl>) {
    return pipe(
        createRpcSubscriptionsTransportFromChannelCreator(
            createChannel,
        ) as RpcSubscriptionsTransport as RpcSubscriptionsTransportFromClusterUrl<TClusterUrl>,
        transport => getRpcSubscriptionsTransportWithSubscriptionCoalescing(transport),
    );
}

export function createRpcSubscriptionsTransportFromChannelCreator<
    TChannelCreator extends RpcSubscriptionsChannelCreator<TOutboundMessage, TInboundMessage>,
    TInboundMessage,
    TOutboundMessage,
>(createChannel: TChannelCreator) {
    return (async ({ execute, signal }) => {
        const channel = await createChannel({ abortSignal: signal });
        return await execute({ channel, signal });
    }) as TChannelCreator extends RpcSubscriptionsChannelCreatorDevnet<TOutboundMessage, TInboundMessage>
        ? RpcSubscriptionsTransportDevnet
        : TChannelCreator extends RpcSubscriptionsChannelCreatorTestnet<TOutboundMessage, TInboundMessage>
          ? RpcSubscriptionsTransportTestnet
          : TChannelCreator extends RpcSubscriptionsChannelCreatorMainnet<TOutboundMessage, TInboundMessage>
            ? RpcSubscriptionsTransportMainnet
            : RpcSubscriptionsTransport;
}
